import { useState } from 'react';
import {
  ArrowRightLeft,
  CheckCircle2,
  MapPinned,
  Route,
  Truck,
  UserRound,
} from 'lucide-react';
import {
  ACTIVE_TRIPS,
  ALTERNATIVE_ROUTES,
  AVAILABLE_DRIVERS,
} from './mockDispatchData';

const selectClass =
  'w-full rounded-lg border border-logi-grid bg-white px-3 py-2 text-xs text-white outline-none transition focus:border-logi-accent/50 focus:ring-1 focus:ring-logi-accent/30';

const labelClass = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-logi-text0';

export default function QuickReroutePanel({ selectedTripId, onSelectTrip, onApply }) {
  const [altRoute, setAltRoute] = useState('');
  const [altDriver, setAltDriver] = useState('');
  const [appliedMsg, setAppliedMsg] = useState('');

  const selectedTrip = ACTIVE_TRIPS.find((t) => t.id === selectedTripId);

  const handleApply = () => {
    if (!selectedTrip) return;
    const routeLabel = altRoute || selectedTrip.route;
    const driverLabel =
      AVAILABLE_DRIVERS.find((d) => d.id === altDriver)?.name || selectedTrip.driver;

    onApply?.({ tripId: selectedTrip.id, route: routeLabel, driver: driverLabel });
    setAppliedMsg(`Re-route applied to ${selectedTrip.id}`);
    setTimeout(() => setAppliedMsg(''), 3500);
  };

  return (
    <aside
      aria-label="Quick re-routing panel"
      className="flex w-full shrink-0 flex-col overflow-hidden rounded-xl border border-logi-grid bg-white shadow-lg xl:w-72 xl:min-h-0 2xl:w-80"
    >
      <div className="shrink-0 border-b border-logi-grid px-4 py-2.5">
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Quick Re-routing</h3>
            <p className="text-[11px] text-logi-text0">Instant route &amp; driver reassignment</p>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
        <div>
          <label className={labelClass}>Select Active Trip</label>
          <select
            className={selectClass}
            value={selectedTripId || ''}
            onChange={(e) => onSelectTrip?.(e.target.value || null)}
          >
            <option value="">Choose trip…</option>
            {ACTIVE_TRIPS.map((trip) => (
              <option key={trip.id} value={trip.id}>
                {trip.id} · {trip.vehicle}
              </option>
            ))}
          </select>
        </div>

        {selectedTrip ? (
          <>
            <div className="rounded-lg border border-logi-grid bg-white p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
                Current Assignment
              </p>
              <div className="mt-2 space-y-1.5 text-xs">
                <p className="flex items-center gap-1.5 text-logi-muted">
                  <Truck className="h-3.5 w-3.5 text-logi-text0" />
                  {selectedTrip.vehicle}
                </p>
                <p className="flex items-center gap-1.5 text-logi-muted">
                  <UserRound className="h-3.5 w-3.5 text-logi-text0" />
                  {selectedTrip.driver}
                </p>
                <p className="flex items-center gap-1.5 text-logi-muted">
                  <Route className="h-3.5 w-3.5 text-logi-text0" />
                  {selectedTrip.route}
                </p>
                <p className="font-mono text-[10px] text-logi-accent/80">{selectedTrip.progress}% complete</p>
              </div>
            </div>

            <div>
              <label className={labelClass}>
                <span className="inline-flex items-center gap-1">
                  <MapPinned className="h-3 w-3" />
                  Alternative Route
                </span>
              </label>
              <select
                className={selectClass}
                value={altRoute}
                onChange={(e) => setAltRoute(e.target.value)}
              >
                <option value="">Keep current route</option>
                {ALTERNATIVE_ROUTES.map((route) => (
                  <option key={route} value={route}>
                    {route}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>
                <span className="inline-flex items-center gap-1">
                  <UserRound className="h-3 w-3" />
                  Alternative Driver
                </span>
              </label>
              <select
                className={selectClass}
                value={altDriver}
                onChange={(e) => setAltDriver(e.target.value)}
              >
                <option value="">Keep current driver</option>
                {AVAILABLE_DRIVERS.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.name} — {driver.status}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleApply}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-logi-accent/30 bg-logi-accent/10 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-logi-text transition hover:bg-logi-accent/20"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Apply Re-route
            </button>

            {appliedMsg && (
              <p className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                {appliedMsg}
              </p>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-logi-grid bg-white px-4 py-8 text-center">
            <Route className="mx-auto h-8 w-8 text-logi-text0" strokeWidth={1.25} />
            <p className="mt-2 text-xs text-logi-text0">
              Select a trip from the monitor table or dropdown to configure re-routing.
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}
